import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import {
  DnsIcon,
  TaniumIcon,
  TrashIcon,
  CircleCheckIcon,
  RefreshIcon,
  SavedQuestionIcon,
  StatePendingIcon,
  StateCriticalIcon,
  ExternalLinkIcon,
} from '@tanium/react-graphics';
import {
  StackedRibbonListMinimalStatic,
  StackedRibbonListStatic,
  StackedRibbonListWrapper,
} from '@tanium/react-stacked-ribbon-list';
import { Label } from '@tanium/react-label';
import { Box } from '@tanium/react-box';
import { css } from '@tanium/style-system';
import styled from '@tanium/react-emotion-9';
import { TooltippedButton } from '@tanium/react-button';
import { PageSection } from '@tanium/react-page-section';
import { Well } from '@tanium/react-well';
import { TextWithIcon } from '@tanium/react-text-with-icon';
import { NavSectionLeaf } from '@tanium/react-workbench';
import { Select, ValueType } from '@tanium/react-select';
import { HEX_477FBA } from '@tanium/colors';
import { Shimmer } from '@tanium/react-shimmer';
import { Notification } from '@tanium/react-persistent-notifications';
import { Stack, StackChild } from '@tanium/react-stack';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { Domain, DomainStatus, TaniumInstance } from '../../types';
import { alwaysTrueOp } from '../../utilities/commonFuncs';
import { formatDate } from '../../utilities/dateHelper';
import { AsyncSuspense } from '../../components/AsyncSuspense';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';
import { useTenantDomains } from '../../hooks/useTenantDomains';
import { DeleteDomainModal } from './DeleteDomainModal';
import { AddDomainModal } from './AddDomainModal';
import { CopyableText } from '../../components/CopyableText';
import { TextWithInfoTooltip } from '../../components/TextWithInfoToolTip';
import { LinkText } from '../../components/LinkText';
import { AsyncStatus } from '../../hooks/useAsync';
import { IdentityProvidersSection } from './IdentityProvidersSection';
import { DocLinks } from '../../utilities/constants';
import { getTaniumConsoleLink } from '../../utilities/instanceHelper';
import { useHasPermissions } from '../../hooks/useUserPermissions';
import { Permission } from '../../models/Permissions';
import { getCloudInstanceRoutes } from '../../Routes';

const Container = styled(Box)(
  css({
    display: 'flex',
    whiteSpace: 'nowrap',
  })
);

export const ManageAccountSection: React.VFC = () => {
  const { t } = useTranslation();
  const cloudRoutes = getCloudInstanceRoutes(t);

  const [deleteDomainModalOpen, setDeleteDomainModalOpen] = useState({ open: false, domainId: '' });
  const [addDomainModalOpen, setAddDomainModalOpen] = useState(false);
  const { loadLinkedTaniumInstances, linkedTaniumInstances } = useTaniumInstances();
  const { loadTenantDomains, checkDomain, tenantDomains } = useTenantDomains();
  const { showNotification } = useToastNotificationsControl();
  const canDeleteDomain = useHasPermissions([Permission.DeleteDomain]);
  const canCreateDomain = useHasPermissions([Permission.CreateDomain]);
  const hasCloudInstances = linkedTaniumInstances?.find((i) => !!i.cloudProvider);

  const cloudRouteOptions = useMemo(() => {
    return cloudRoutes.map((route) => ({
      value: route,
      label: route.name,
    }));
  }, [cloudRoutes]);

  const taniumInstancesSection = () => {
    const instances = linkedTaniumInstances;

    const openInNewWindow = (itemData: TaniumInstance) => {
      const ceDomain = itemData.displayName;
      window.open(getTaniumConsoleLink(ceDomain), '_blank', 'noopener,noreferrer');
    };

    const goToInstance = (itemData: TaniumInstance, route: NavSectionLeaf) => {
      window.location.href = `#${route.url}?id=${itemData.id}`;
    };

    return (
      <PageSection title={<TextWithIcon leadingIcon={TaniumIcon}>{t('Tanium Instances')}</TextWithIcon>}>
        <AsyncSuspense
          state={loadLinkedTaniumInstances}
          loading={
            <Box mt={2} height={1} width="100%">
              <Shimmer />
            </Box>
          }
          failure={t('Failed to load Tanium Instances')}
        >
          <div>
            {instances.length ? (
              <StackedRibbonListWrapper>
                <StackedRibbonListMinimalStatic
                  columns={[
                    {
                      label: t('License ID'),
                      dataKey: 'licenseId',
                      grow: 1,
                      isSortDisabled: true,
                    },
                    {
                      label: t('Hostname'),
                      dataKey: 'displayName',
                      grow: 1,
                      noWrap: true,
                      isSortDisabled: true,
                    },
                    {
                      label: t('Linked Date'),
                      dataKey: 'createdAt',
                      grow: 1,
                      isSortDisabled: true,
                      renderer: ({ itemData }) => {
                        return formatDate(new Date(itemData.createdAt));
                      },
                    },
                    {
                      label: t('Actions'),
                      isSortDisabled: true,
                      width: /* istanbul ignore next */ hasCloudInstances ? 440 : 150,
                      dataKey: 'actions',
                      alignment: 'left',
                      renderer: ({ itemData }) => {
                        const isCloudInstance = !!itemData.cloudProvider;

                        return (
                          <Box>
                            <Container>
                              {isCloudInstance && (
                                <TooltippedButton
                                  size="sm"
                                  style={{ marginLeft: '10px' }}
                                  variant="secondary"
                                  onClick={() => openInNewWindow(itemData)}
                                >
                                  {t('Go to Console')}
                                  <Box as={ExternalLinkIcon} width={2} height={1} color={HEX_477FBA}></Box>
                                </TooltippedButton>
                              )}
                              {isCloudInstance && (
                                <Box marginLeft={'10px'}>
                                  <Select
                                    size="sm"
                                    aria-label={t('Manage Instance')}
                                    placeholder={t('Manage Instance')}
                                    options={cloudRouteOptions as []}
                                    isMulti={false}
                                    menuPosition="fixed"
                                    styles={{
                                      container: /* istanbul ignore next */ (base) => {
                                        return {
                                          ...base,
                                          width: 150,
                                          borderRadius: '5px',
                                          border: `${HEX_477FBA} 1px solid`,
                                          color: HEX_477FBA,
                                        };
                                      },
                                      /* istanbul ignore next */
                                      placeholder: /* istanbul ignore next */ (base) => {
                                        return {
                                          ...base,
                                          color: HEX_477FBA,
                                          font: '12px Arial',
                                          fontWeight: 'bold',
                                        };
                                      },
                                    }}
                                    onChange={(valueType: ValueType<NavSectionLeaf>, _action) => {
                                      goToInstance(itemData, (valueType as unknown as { value: NavSectionLeaf }).value);
                                    }}
                                  />
                                </Box>
                              )}
                            </Container>
                          </Box>
                        );
                      },
                    },
                  ]}
                  dataGetter={(i: number) => instances[i]}
                  isItemLoaded={alwaysTrueOp}
                  itemCount={instances.length}
                  itemIdGetter={(i: number) => instances[i].id}
                ></StackedRibbonListMinimalStatic>
              </StackedRibbonListWrapper>
            ) : (
              <Notification role="note" type="info">
                {t('No Tanium Instances are linked to this account.')}
              </Notification>
            )}
          </div>
        </AsyncSuspense>
      </PageSection>
    );
  };

  const DnsRecordNameValueSection: React.VFC<{
    domainName: string;
    dnsRecordValue: string;
  }> = ({ domainName: dnsRecordName, dnsRecordValue }) => {
    return (
      <Stack itemSpacing={4} itemDividers>
        <Stack itemSpacing={1} direction="column">
          <TextWithInfoTooltip
            text={t('TXT Record Name')}
            helpText={t(
              'DNS record interfaces vary by domain provider, some providers refer to the Name field as Host.'
            )}
          />
          <CopyableText text={dnsRecordName} bold />
        </Stack>
        <Stack itemSpacing={1} direction="column">
          <TextWithInfoTooltip
            text={t('TXT Record Value')}
            helpText={t(
              'Setting the unique identifier in the Value field of the TXT Record serves as confirmation to Tanium that you are the owner of the domain.'
            )}
          />
          <CopyableText text={dnsRecordValue} bold />
        </Stack>
      </Stack>
    );
  };

  useEffect(() => {
    if (checkDomain.status === AsyncStatus.Success) {
      checkDomain.reset();
      showNotification({
        type: 'info',
        contents: t('Refreshed Domain Verification status'),
      });
    } else if (checkDomain.status === AsyncStatus.Failure) {
      checkDomain.reset();
      showNotification({
        type: 'error',
        contents: t('Failed to refresh Domain Verification status'),
      });
    }
  }, [t, checkDomain.status, loadTenantDomains, checkDomain, showNotification]);

  const verifiedDomainsSection = () => {
    const domains = tenantDomains;
    const defaultExpansion = new Set(
      domains.filter((item) => item.verificationStatus === DomainStatus.PENDING).map((item) => item.domainName)
    );
    const tooltipAddDomain = canCreateDomain ? '' : t('You have insufficient permissions to add a domain.');
    const tooltipDeleteDomain = canDeleteDomain ? '' : t('You have insufficient permissions to delete a domain.');

    return (
      <PageSection
        title={<TextWithIcon leadingIcon={DnsIcon}>{t('Domains')}</TextWithIcon>}
        additionalTitleContent={
          <Stack width="100%" direction="row" justifyContent="right" alignItems="center">
            <TooltippedButton
              size="sm"
              onClick={() => setAddDomainModalOpen(true)}
              disabled={!canCreateDomain}
              tooltipContent={tooltipAddDomain}
            >
              {t('Add Domain')}
            </TooltippedButton>
          </Stack>
        }
      >
        <AsyncSuspense
          state={loadTenantDomains}
          loading={
            <Box mt={2} height={1} width="100%">
              <Shimmer />
            </Box>
          }
          failure={t('Failed to load Domains')}
        >
          <>
            {domains.length ? (
              <StackedRibbonListStatic<string, Domain>
                expandableContent={{
                  renderer: ({ itemData }) => {
                    if (itemData.verificationStatus === DomainStatus.PENDING) {
                      return (
                        <Stack itemSpacing={2} itemDividers direction="column">
                          <Trans
                            i18nKey="To verify this domain, add a TXT record to your DNS with the Name and Value below. DNS propagation can take up to 48 hours. See Help with TXT records."
                            components={{ helpLink: <LinkText to={DocLinks.VERIFY_DOMAIN} /> }}
                          >
                            {/* tests fail unless something is in here */}
                            To verify this domain
                          </Trans>
                          <StackChild>
                            <DnsRecordNameValueSection
                              domainName={itemData.domainName}
                              dnsRecordValue={itemData.dnsRecordValue}
                            />
                          </StackChild>
                        </Stack>
                      );
                    }
                    return (
                      <DnsRecordNameValueSection
                        domainName={itemData.domainName}
                        dnsRecordValue={itemData.dnsRecordValue}
                      />
                    );
                  },
                }}
                columns={[
                  {
                    label: t('Status'),
                    dataKey: 'verificationStatus',
                    isSortDisabled: true,
                    alignment: 'center',
                    width: 60,
                    renderer: ({ itemData }) => {
                      switch (itemData.verificationStatus) {
                        case DomainStatus.SUCCESS:
                          return <CircleCheckIcon height={16} width={16} color="green" />;
                        case DomainStatus.PENDING:
                          return <StatePendingIcon height={16} width={16} color="gray" />;
                        case DomainStatus.ERROR:
                        case DomainStatus.CONFLICT:
                          return <StateCriticalIcon height={16} width={16} color="red" />;
                        default:
                          return <SavedQuestionIcon height={16} width={16} />;
                      }
                    },
                  },
                  {
                    label: t('Domain Name'),
                    dataKey: 'domainName',
                    isSortDisabled: true,
                  },
                  {
                    label: t('Domain In Use By...'),
                    dataKey: 'inUseBy',
                    isSortDisabled: true,
                    renderer: ({ itemData }) => {
                      return (
                        <Box>
                          {itemData.domainUsages.length > 0
                            ? itemData.domainUsages.map((x) => {
                                return <Label key={x}>{x}</Label>;
                              })
                            : t('None')}
                        </Box>
                      );
                    },
                  },
                  {
                    label: t('Actions'),
                    dataKey: 'actions',
                    isSortDisabled: true,
                    alignment: 'center',
                    width: 80,
                    renderer: ({ itemData }) => {
                      return (
                        <>
                          {itemData.verificationStatus === DomainStatus.PENDING && (
                            <>
                              <TooltippedIconButton
                                variant="primary"
                                label={t('Refresh Status')}
                                icon={RefreshIcon}
                                width={16}
                                height={16}
                                onClick={async () => {
                                  await checkDomain.execute(itemData.id);
                                }}
                                disabled={checkDomain.status === AsyncStatus.Pending}
                              />
                              &nbsp;
                            </>
                          )}
                          <TooltippedIconButton
                            variant="primary"
                            label={t('Delete Domain')}
                            icon={TrashIcon}
                            width={16}
                            height={16}
                            disabled={itemData.domainUsages.length > 0 || !canDeleteDomain}
                            tooltipContent={itemData.domainUsages.length ? t('Domain is in use') : tooltipDeleteDomain}
                            onClick={() => setDeleteDomainModalOpen({ open: true, domainId: itemData.id })}
                          />
                        </>
                      );
                    },
                  },
                ]}
                dataGetter={(i: number) => domains[i]}
                isItemLoaded={alwaysTrueOp}
                itemCount={domains.length}
                itemIdGetter={(i: number) => domains[i].domainName}
                defaultExpansion={defaultExpansion}
              ></StackedRibbonListStatic>
            ) : (
              <Notification role="note" type="info">
                {t('Add and verify domains to enable their use with shared cloud services.')}
              </Notification>
            )}
          </>
        </AsyncSuspense>
      </PageSection>
    );
  };

  return (
    <div>
      <Well mt={1} p={1}>
        {taniumInstancesSection()}
      </Well>
      <Well mt={1} p={1}>
        {verifiedDomainsSection()}
      </Well>
      <Well mt={1} p={1}>
        <IdentityProvidersSection />
      </Well>
      <DeleteDomainModal
        domainId={deleteDomainModalOpen.domainId}
        isOpen={deleteDomainModalOpen.open}
        onRequestClose={() => setDeleteDomainModalOpen({ open: false, domainId: '' })}
      />
      <AddDomainModal isOpen={addDomainModalOpen} onRequestClose={() => setAddDomainModalOpen(false)} />
    </div>
  );
};
